import { z } from 'zod';

// The value grammar every theme section's settings obey, whoever wrote the JSON.
// These values are rendered straight into a public storefront page, so the rules are
// enforced by key name: colours are hex, sizes are tokens or CSS lengths, media is http(s).

export const SIZE_TOKENS = ['none', 'xs', 'sm', 'md', 'lg', 'xl', 'pill'] as const;

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const CSS_LENGTH_RE = /^-?\d+(?:\.\d+)?(?:px|rem|em|%|vw|vh)$/;
const HTTP_URL_RE = /^https?:\/\/[^\s"'<>{}\\]+$/i;
// Media may also be a root-relative path, so a theme can carry the platform's own seeded demo assets
// (`/uploads/demo-shop/…`) without baking a hostname into its JSON.
const MEDIA_URL_RE = /^(?:https?:\/\/[^\s"'<>{}\\]+|\/(?!\/)[^\s"'<>{}\\]*)$/i;
const LINK_RE = /^(?:https?:\/\/[^\s"'<>{}\\]+|\/(?!\/)[^\s"'<>{}\\]*|#[A-Za-z0-9_-]+)$/;
const UNSAFE_RE = /javascript:|vbscript:|data:text\/html|<script|on(?:error|load|click)\s*=/i;

const COLOR_KEY_RE = /(?:colour|color|background)$/i;
// Plural forms are spelled out because a list of bare URLs is walked under its own key, not the item's:
// `payments: [...]` checks every item as "payments", so a singular-only rule would never see it.
const MEDIA_KEY_RE = /^(?:urls?|srcs?|avatars?|posters?|logos?|payments?|photos?|thumbnails?)$|images?$/i;
// The same camelCase-compound idea `NUMBER_SUFFIX_RE` uses below, for the media a second one names: a
// picture that has one crop for a phone and another for everything else stores the phone's as
// `mobileSrc`, which the closed list above cannot see. Case-sensitive and preceded by a lowercase
// letter, so only a compound matches — `mobileSrc` and `hoverImage` are media, a bare `source` is not.
const MEDIA_SUFFIX_RE = /[a-z0-9](?:Src|Image|Photo|Poster|Logo)$/;
const LINK_KEY_RE = /^href$/i;
// `x`, `y`, `blur` and `spread` are the shadow group's four offsets: lengths whose key ends in no size word,
// so without naming them here a shadow would be the one part of a block that ships unvalidated.
// `headingFontSize` matches `fontsize`; `headingSize` matches `headingsize` — both are optional per-section type sizes.
const SIZE_KEY_RE = /^(?:width|top|bottom|inline|x|y|blur|spread)$|(?:radius|maxwidth|fontsize|quotesize|headingsize|height)$/i;
// A radius is one size, or exactly four corner sizes. The items are checked under the key `radius` like any
// other list, so the only thing left to police is the count — three or five corners is a typo, not a shorthand.
const RADIUS_KEY_RE = /radius$/i;
const CORNER_COUNT = 4;
// An aspect ratio is `auto` or W/H, free-form because reproducing a reference needs its exact box —
// a 1140x553 lede image is 1140/553, which no fixed token expresses. It lands in CSS `aspect-ratio`.
const RATIO_KEY_RE = /^ratio$/i;
const RATIO_RE = /^(?:auto|\d{1,5}\/\d{1,5})$/;
// Builder counts: a string `limit` interpolated into a <style> tag closed the rule and appended CSS.
const NUMBER_KEY_RE = /^(?:limit|columns|thumbLimit|unitsLeft|barPercent|headingWeight|opacity|rating|quantity|likes|speedSeconds)$/i;

// A closed list forces every new number to borrow a name that already exists — a popup's delay stored
// as `speedSeconds` because that was the only duration the rule knew. These suffixes let a setting say
// what it is. Deliberately case-sensitive and preceded by a lowercase letter, so only a camelCase
// compound matches: `delaySeconds` and `scrollPercent` are numbers, `discount` and a label named
// `seconds` are not — which a case-insensitive suffix would have wrongly claimed.
const NUMBER_SUFFIX_RE = /[a-z0-9](?:Seconds|Percent|Count)$/;

// `showCount` is a switch, not a count. A key that opens with one of these reads as a question, so the
// suffix rule stands aside for it rather than forcing a rename that would make the setting read worse.
const BOOLEAN_PREFIX_RE = /^(?:show|hide|is|has|can|use|enable|allow)[A-Z]/;

const MAX_DEPTH = 8;
const MAX_LIST = 200;
const MAX_KEYS = 80;
const MAX_STRING = 5000;

export const HexColorSchema = z.string().regex(HEX_RE, 'Colours must be a hex string, e.g. #1b2e26');
export const CssLengthSchema = z.string().regex(CSS_LENGTH_RE, 'Sizes must be a CSS length, e.g. 16px');
export const SizeValueSchema = z.union([z.enum(SIZE_TOKENS), CssLengthSchema]);
export const ImageUrlSchema = z.string().regex(HTTP_URL_RE, 'Images must be an http(s) URL');
export const LinkUrlSchema = z.string().regex(LINK_RE, 'Links must be an http(s) URL, a /path or a #anchor');
export const FontFamilySchema = z.string().min(1).max(120).regex(/^[A-Za-z0-9 ,'"-]+$/, 'Font names may only contain letters, digits, spaces, commas, quotes and dashes');

/** Where a walk is, and whether it holds named counts to numbers: an element's props do, a theme's tokens do not. */
type WalkContext = { ctx: z.RefinementCtx; path: (string | number)[]; depth: number; counts?: boolean };

// Records one problem against the exact key that carries it.
function fail(walk: WalkContext, message: string): void {
  walk.ctx.addIssue({ code: z.ZodIssueCode.custom, path: walk.path, message });
}

// Checks one string setting against the rule its key implies, and against script injection in every case.
function checkStringLeaf(key: string, value: string, walk: WalkContext): void {
  if (value.length > MAX_STRING) return fail(walk, `A setting may not exceed ${MAX_STRING} characters`);
  if (UNSAFE_RE.test(value)) return fail(walk, 'A setting may not contain a script, event handler or data: URL');
  if (value === '') return;
  if (COLOR_KEY_RE.test(key) && !HEX_RE.test(value)) return fail(walk, `"${key}" must be a hex colour`);
  if ((MEDIA_KEY_RE.test(key) || MEDIA_SUFFIX_RE.test(key)) && !MEDIA_URL_RE.test(value)) return fail(walk, `"${key}" must be an http(s) URL or a /path`);
  if (LINK_KEY_RE.test(key) && !LINK_RE.test(value)) return fail(walk, `"${key}" must be an http(s) URL, a /path or a #anchor`);
  if (RATIO_KEY_RE.test(key) && !RATIO_RE.test(value)) return fail(walk, `"${key}" must be "auto" or a width/height ratio, e.g. 16/9`);
  if (SIZE_KEY_RE.test(key) && !CSS_LENGTH_RE.test(value) && !(SIZE_TOKENS as readonly string[]).includes(value)) {
    return fail(walk, `"${key}" must be a size token or a CSS length`);
  }
}

// Walks a list setting, checking every item under the list's own key.
function walkList(key: string, items: readonly unknown[], walk: WalkContext): void {
  if (items.length > MAX_LIST) return fail(walk, `A list may not hold more than ${MAX_LIST} items`);
  if (RADIUS_KEY_RE.test(key) && items.length !== CORNER_COUNT) {
    return fail(walk, `"${key}" must be one size, or exactly ${CORNER_COUNT} corner sizes`);
  }
  items.forEach((item, index) => walkSettingValue(key, item, { ...walk, path: [...walk.path, index], depth: walk.depth + 1 }));
}

// Walks a group setting, checking every key it holds.
function walkSettingGroup(group: Record<string, unknown>, walk: WalkContext): void {
  const keys = Object.keys(group);
  if (keys.length > MAX_KEYS) return fail(walk, `A settings group may not hold more than ${MAX_KEYS} keys`);
  for (const key of keys) {
    walkSettingValue(key, group[key], { ...walk, path: [...walk.path, key], depth: walk.depth + 1 });
  }
}

// Walks one setting value: groups and lists recurse, leaves are checked, anything else is rejected.
function walkSettingValue(key: string, value: unknown, walk: WalkContext): void {
  if (walk.depth > MAX_DEPTH) return fail(walk, 'Settings are nested too deeply');
  // 1. Groups and lists recurse even when the key is a number word — footer `columns` is a list of links.
  if (Array.isArray(value)) return walkList(key, value, walk);
  if (typeof value === 'object' && value !== null) return walkSettingGroup(value as Record<string, unknown>, walk);
  // 2. Builder counts: a named number key may only hold a finite number, never a string or a switch.
  if (walk.counts !== false && (NUMBER_KEY_RE.test(key) || (NUMBER_SUFFIX_RE.test(key) && !BOOLEAN_PREFIX_RE.test(key)))) {
    return typeof value === 'number' && Number.isFinite(value)
      ? undefined
      : fail(walk, `"${key}" must be a finite number`);
  }
  if (typeof value === 'string') return checkStringLeaf(key, value, walk);
  if (typeof value === 'number') return Number.isFinite(value) ? undefined : fail(walk, 'Numbers must be finite');
  if (typeof value === 'boolean' || value === null) return;
  return fail(walk, 'A setting must be text, a number, a switch, a group or a list');
}

/** A section's settings: named groups, lists and primitives — never a function, a node or free-form CSS. */
export const ElementPropsSchema = z
  .record(z.string().min(1).max(64), z.unknown())
  .superRefine((settings, ctx) => walkSettingGroup(settings, { ctx, path: [], depth: 0 }));

export type ElementPropsDto = z.infer<typeof ElementPropsSchema>;

/**
 * A theme's tokens, walked with the same safety rules as a section's settings — no script, no over-long
 * text, nothing nested past reach — but without the element counts: a colour named `rating` is a colour,
 * and reading it as the stars' count refused every live repaint of a theme that names one.
 */
export const StyleTokensGrammarSchema = z
  .record(z.string().min(1).max(64), z.unknown())
  .superRefine((settings, ctx) => walkSettingGroup(settings, { ctx, path: [], depth: 0, counts: false }));
